<template>
    <div :id="getId('modal_outer')">
        <transition
            @beforeEnter="onBeforeEnter"
            @enter="onEnter"
            @afterEnter="onAfterEnter"
            @beforeLeave="onBeforeLeave"
            @afterLeave="onAfterLeave"
        >
            <div
                ref="modal"
                :class="modalClasses"
                v-bind="dialogAttributes"
                @mousedown="onClickOut"
            >
                <div :class="dialogClasses">
                    <div
                        ref="content"
                        class="modal-content"
                        role="document"
                        tabindex="-1"
                        @focusout="onFocusout"
                        @click.stop
                        @mousedown.stop
                    >
                        <template v-if="!hideHeader">
                            <header :id="getId('modal_header')" class="modal-header">
                                <slot name="modal-header">
                                    <dynamic-element :tag="titleTag" class="modal-title">
                                        <slot name="modal-title">
                                            {{ title }}
                                        </slot>

                                        <f-button-close
                                            v-if="!hideHeaderClose"
                                            :disabled="is_transitioning"
                                            :aria-label="headerCloseLabel"
                                            :text-variant="headerTextVariant"
                                            @click="e => hide('header-close')"
                                        >
                                            <slot name="modal-header-close" />
                                        </f-button-close>
                                    </dynamic-element>
                                </slot>
                            </header>
                        </template>

                        <div :id="getId('modal_body')" ref="body" :class="bodyClasses">
                            <slot />
                        </div>

                        <div
                            v-if="!hideFooter"
                            :id="getId('modal_footer')"
                            ref="footer"
                            :class="footerClasses"
                        >
                            <slot name="modal-footer" :hide="hide">
                                <f-button
                                    v-if="!okOnly"
                                    :variant="cancelVariant"
                                    :size="buttonSize"
                                    :disabled="cancelDisabled || busy || is_transitioning"
                                    @click="(e) => hide('cancel')"
                                >
                                    <slot name="modal-cancel">
                                        {{ cancelTitle }}
                                    </slot>
                                </f-button>

                                <f-button
                                    :variant="okVariant"
                                    :size="buttonSize"
                                    :disabled="okDisabled || busy || is_transitioning"
                                    @click="(e) => hide('ok')"
                                >
                                    <slot name="modal-ok">{{ okTitle }}</slot>
                                </f-button>
                            </slot>
                        </div>
                    </div>
                </div>
            </div>
        </transition>

        <div v-if="!is_transitioning" :id="getId('modal_backdrop')" :class="backdropClasses" />
    </div>
</template>

<script>
import { mapMutations } from 'vuex';

import idMixin from '@/mixins/id';
import observeDom from '@/utils/observe-dom';
import DynamicElement from '@/components/utilities/dynamic-element';
import fButton from '@/components/shared/modal/modal-button';
import fButtonClose from '@/components/shared/modal/modal-button-close';
import { HIDE_MODAL } from '@/store/types/mutation-types';
import {
    selectAll,
    getBCR,
    addClass,
    removeClass,
    setAttr,
    removeAttr,
    getAttr,
    hasAttr,
    eventOn,
    eventOff,
} from '@/utils/dom';

function defineProperty(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value,
            enumerable: true,
            configurable: true,
            writable: true,
        });
    } else {
        obj[key] = value;
    }
    return obj;
}

// Selectors for padding/margin adjustments
const Selector = {
    FIXED_CONTENT: '.fixed-top, .fixed-bottom, .sticky-top',
    STICKY_CONTENT: '.sticky-top',
    NAVBAR_TOGGLER: '.navbar-toggler',

    // ObserveDom config
};

const OBSERVER_CONFIG = {
    subtree: true,
    childList: true,
    characterData: true,
    attributes: true,
    attributeFilter: ['style', 'class'],
};

export default {
    components: {
        fButton,
        fButtonClose,
        DynamicElement,
    },

    mixins: [idMixin],

    props: {
        ariaLabelledby: {
            type: String,
            default: '',
        },

        bodyBgVariant: {
            type: String,
            default: null,
        },

        bodyClass: {
            type: [String, Array],
            default: null,
        },

        bodyTextVariant: {
            type: String,
            default: null,
        },

        busy: {
            type: Boolean,
            default: false,
        },

        buttonSize: {
            type: String,
            default: '',
        },

        cancelDisabled: {
            type: Boolean,
            default: false,
        },

        cancelVariant: {
            type: String,
            default: 'secondary',
        },

        cancelTitle: {
            type: String,
            default: 'Cancel',
        },

        centered: {
            type: Boolean,
            default: false,
        },

        footerBgVariant: {
            type: String,
            default: null,
        },

        footerBorderVariant: {
            type: String,
            default: null,
        },

        footerTextVariant: {
            type: String,
            default: null,
        },

        footerClass: {
            type: [String, Array],
            default: null,
        },

        headerBgVariant: {
            type: String,
            default: null,
        },

        headerClass: {
            type: [String, Array],
            default: null,
        },

        headerCloseLabel: {
            type: String,
            default: 'Close',
        },

        headerBorderVariant: {
            type: String,
            default: null,
        },

        headerTextVariant: {
            type: String,
            default: null,
        },

        hideHeader: {
            type: Boolean,
            default: false,
        },

        hideHeaderClose: {
            type: Boolean,
            default: false,
        },

        hideFooter: {
            type: Boolean,
            default: false,
        },

        noFade: {
            type: Boolean,
            default: false,
        },

        noCloseOnBackdrop: {
            type: Boolean,
            default: false,
        },

        noCloseOnEsc: {
            type: Boolean,
            default: false,
        },

        noEnforceFocus: {
            type: Boolean,
            default: false,
        },

        okDisabled: {
            type: Boolean,
            default: false,
        },

        okOnly: {
            type: Boolean,
            default: false,
        },

        okTitle: {
            type: String,
            default: 'OK',
        },

        okVariant: {
            type: String,
            default: 'primary',
        },

        size: {
            type: String,
            default: 'xl',
        },

        title: {
            type: String,
            default: '',
        },

        titleTag: {
            type: String,
            default: 'h5',
        },
    },

    data() {
        return {
            is_transitioning: false,
            is_block: false,
            scrollbarWidth: 0,
            isBodyOverflowing: false,
            focusBackIn: true,
        };
    },

    computed: {
        dialogAttributes() {
            const attributes = {
                id: this.getId(),
                role: 'dialog',
            };

            if (this.hideHeader) {
                // Allow aria-labelledby to be set manually
                if (this.ariaLabelledby !== '') {
                    attributes['aria-labelledby'] = this.ariaLabelledby;
                } else {
                    attributes['aria-label'] = this.$slots['modal-title'] || this.title;
                }
            } else {
                attributes['aria-labelledby'] = this.getId('modal_header');
            }

            return attributes;
        },

        modalClasses() {
            return [
                'modal',
                {
                    fade: !this.noFade,
                    'd-block': this.is_block,
                },
            ];
        },

        dialogClasses() {
            const ref = {};

            return [
                'modal-dialog',
                (ref,
                defineProperty(ref, `modal-${ this.size }`, Boolean(this.size)),
                defineProperty(ref, 'modal-dialog-centered', this.centered),
                ref),
            ];
        },

        backdropClasses() {
            return [
                'modal-backdrop',
                'show',
                { fade: !this.noFade },
            ];
        },

        bodyClasses() {
            const ref3 = {};

            return [
                'modal-body',
                (ref3,
                defineProperty(
                    ref3,
                    `f-bg-${ this.bodyBgVariant }`,
                    Boolean(this.bodyBgVariant),
                ),
                defineProperty(
                    ref3,
                    `f-text-${ this.bodyTextVariant }`,
                    Boolean(this.bodyTextVariant),
                ),
                ref3),
                this.bodyClass,
            ];
        },

        footerClasses() {
            const ref4 = {};

            return [
                'modal-footer',
                (ref4,
                defineProperty(
                    ref4,
                    `f-bg-${ this.footerBgVariant }`,
                    Boolean(this.footerBgVariant),
                ),
                defineProperty(
                    ref4,
                    `f-text-${ this.footerTextVariant }`,
                    Boolean(this.footerTextVariant),
                ),
                defineProperty(
                    ref4,
                    `f-border-${ this.footerBorderVariant }`,
                    Boolean(this.footerBorderVariant),
                ),
                ref4),
                this.footerClass,
            ];
        },
    },

    created() {
        // create non-reactive property
        this.observer = null;
        this.$root.$on('toggle-focus-back-in', this.toggleFocusBackIn);
    },

    mounted() {
        // Measure scrollbar
        this.getScrollbarWidth();
        this.show();
    },

    beforeDestroy() {
        // Ensure everything is back to normal
        if (this.observer) {
            this.observer.disconnect();
            this.observer = null;
        }

        this.setResizeEvent(false);

        // Re-adjust body/navbar/fixed padding/margins (if needed)
        removeClass(document.body, 'modal-open');
        this.resetAdjustments();
        this.resetScrollbar();
    },

    methods: {
        ...mapMutations({
            hideModal: HIDE_MODAL,
        }),

        async show() {
            this.$emit('shown');
            this.$root.$emit('flex::modal::shown');

            await this.$nextTick();

            // We do this after nextTick to ensure the modal is in DOM first before we show it
            this.$emit('change', true);

            // Observe changes in modal content and adjust if necessary
            this.observer = observeDom(
                this.$refs.content,
                this.adjustDialog.bind(this),
                OBSERVER_CONFIG,
            );
        },

        hide(trigger) {
            if (trigger === 'ok') this.$emit('ok');
            if (trigger === 'cancel') this.$emit('cancel');

            this.$emit('hidden');
            this.$root.$emit('flex::modal::hidden');

            // stop observing for content changes
            if (this.observer) {
                this.observer.disconnect();
                this.observer = null;
            }

            this.$emit('change', false);
            this.hideModal();
        },

        // Transition Handlers
        onBeforeEnter() {
            this.is_transitioning = true;
            this.checkScrollbar();
            this.setScrollbar();
            this.adjustDialog();
            addClass(document.body, 'modal-open');
            this.setResizeEvent(true);
        },

        onEnter() {
            this.is_block = true;
            this.$refs.modal.scrollTop = 0;
        },

        onAfterEnter() {
            this.is_transitioning = false;
            this.$nextTick(() => {
                this.focusFirst();
                this.$emit('shown');
                this.$root.$emit('flex::modal::shown');
            });
        },

        onBeforeLeave() {
            this.is_transitioning = true;
            this.setResizeEvent(false);
        },

        async onAfterLeave() {
            this.is_block = false;
            this.resetAdjustments();
            this.resetScrollbar();
            this.is_transitioning = false;
            removeClass(document.body, 'modal-open');

            await this.$nextTick();

            this.$emit('hidden');
            this.$root.$emit('flex::modal::hidden');
        },

        // UI Event Handlers
        onClickOut() {
            // If backdrop clicked, hide modal
            if (!this.noCloseOnBackdrop) {
                this.hide('backdrop');
            }
        },

        toggleFocusBackIn(focusBackInFlag) {
            this.focusBackIn = focusBackInFlag;
        },

        onFocusout(evt) {
            if (this.focusBackIn === false) return;
            // If focus leaves modal, bring it back
            // 'focusout' Event Listener bound on content
            const content = this.$refs.content;
            if (
                !this.noEnforceFocus
                && content
                && !content.contains(evt.relatedTarget)
            ) {
                content.focus();
            }
        },

        // Resize Listener
        setResizeEvent(on) {
            ['resize', 'orientationchange'].forEach((evtName) => {
                if (on) {
                    eventOn(window, evtName, this.adjustDialog);
                } else {
                    eventOff(window, evtName, this.adjustDialog);
                }
            });
        },

        // Focus control handlers
        focusFirst() {
            // Don't try and focus if we are SSR
            if (typeof document === 'undefined') {
                return;
            }
            const content = this.$refs.content;
            const modal = this.$refs.modal;
            const activeElement = document.activeElement;
            if (activeElement && content && content.contains(activeElement)) {
                // If activeElement is child of content, no need to change focus
            } else if (content) {
                if (modal) {
                    modal.scrollTop = 0;
                }
                // Focus the modal content wrapper
                content.focus();
            }
        },

        // Utility methods
        getScrollbarWidth() {
            const scrollDiv = document.createElement('div');
            scrollDiv.className = 'modal-scrollbar-measure';
            document.body.appendChild(scrollDiv);
            this.scrollbarWidth = scrollDiv.getBoundingClientRect().width - scrollDiv.clientWidth;
            document.body.removeChild(scrollDiv);
        },

        adjustDialog() {
            const modal = this.$refs.modal;
            const isModalOverflowing = modal.scrollHeight > document.documentElement.clientHeight;
            if (!this.isBodyOverflowing && isModalOverflowing) {
                modal.style.paddingLeft = `${ this.scrollbarWidth }px`;
            }
            if (this.isBodyOverflowing && !isModalOverflowing) {
                modal.style.paddingRight = `${ this.scrollbarWidth }px`;
            }
        },

        resetAdjustments() {
            const modal = this.$refs.modal;
            if (modal) {
                modal.style.paddingLeft = '';
                modal.style.paddingRight = '';
            }
        },

        checkScrollbar() {
            const rect = getBCR(document.body);
            this.isBodyOverflowing = rect.left + rect.right < window.innerWidth;
        },

        setScrollbar() {
            if (this.isBodyOverflowing) {
                // Note: DOMNode.style.paddingRight returns the actual value or '' if not set
                //   while $(DOMNode).css('padding-right') returns the calculated value or 0 if not set
                const computedStyle = window.getComputedStyle;
                const body = document.body;
                const scrollbarWidth = this.scrollbarWidth;
                // Adjust fixed content padding
                selectAll(Selector.FIXED_CONTENT).forEach((el) => {
                    const actualPadding = el.style.paddingRight;
                    const calculatedPadding = computedStyle(el).paddingRight || 0;
                    setAttr(el, 'data-padding-right', actualPadding);
                    el.style.paddingRight = `${ parseFloat(calculatedPadding)
                        + scrollbarWidth }px`;
                });
                // Adjust sticky content margin
                selectAll(Selector.STICKY_CONTENT).forEach((el) => {
                    const actualMargin = el.style.marginRight;
                    const calculatedMargin = computedStyle(el).marginRight || 0;
                    setAttr(el, 'data-margin-right', actualMargin);
                    el.style.marginRight = `${ parseFloat(calculatedMargin)
                        - scrollbarWidth }px`;
                });
                // Adjust navbar-toggler margin
                selectAll(Selector.NAVBAR_TOGGLER).forEach((el) => {
                    const actualMargin = el.style.marginRight;
                    const calculatedMargin = computedStyle(el).marginRight || 0;
                    setAttr(el, 'data-margin-right', actualMargin);
                    el.style.marginRight = `${ parseFloat(calculatedMargin)
                        + scrollbarWidth }px`;
                });
                // Adjust body padding
                const actualPadding = body.style.paddingRight;
                const calculatedPadding = computedStyle(body).paddingRight;
                setAttr(body, 'data-padding-right', actualPadding);
                body.style.paddingRight = `${ parseFloat(calculatedPadding)
                    + scrollbarWidth }px`;
            }
        },

        resetScrollbar() {
            // Restore fixed content padding
            selectAll(Selector.FIXED_CONTENT).forEach((el) => {
                if (hasAttr(el, 'data-padding-right')) {
                    el.style.paddingRight = getAttr(el, 'data-padding-right') || '';
                    removeAttr(el, 'data-padding-right');
                }
            });
            // Restore sticky content and navbar-toggler margin
            selectAll(
                `${ Selector.STICKY_CONTENT }, ${ Selector.NAVBAR_TOGGLER }`,
            ).forEach((el) => {
                if (hasAttr(el, 'data-margin-right')) {
                    el.style.marginRight = getAttr(el, 'data-margin-right') || '';
                    removeAttr(el, 'data-margin-right');
                }
            });

            // Restore body padding
            const body = document.body;

            if (hasAttr(body, 'data-padding-right')) {
                body.style.paddingRight = getAttr(body, 'data-padding-right') || '';
                removeAttr(body, 'data-padding-right');
            }
        },
    },
};
</script>

<style lang="scss">
.flex-modal {
    .modal-dialog {
        min-width: 25rem;
        max-width: 60rem;
    }

    &--alert .modal-dialog,
    &--prompt .modal-dialog {
        max-width: 45rem;
    }

    &__description {
        padding: 2rem;
    }

    &__button {
        &--continue {
            @extend %button;
        }

        &--cancel {
            @extend %button-alt;
        }

        &[disabled] {
            opacity: 0.5;
            cursor: default;
        }
    }
}
ß
// Kill the scroll on the body
.modal-open {
    overflow: hidden;
}

// Container that the modal scrolls within
.modal {
    position: fixed;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    z-index: $zindex-modal;
    overflow: hidden;
    // Prevent Chrome on Windows from adding a focus outline. For details, see
    // https://github.com/twbs/bootstrap/pull/10951.
    outline: 0;
    // We deliberately don't use `-webkit-overflow-scrolling: touch;` due to a
    // gnarly iOS Safari bug: https://bugs.webkit.org/show_bug.cgi?id=158342
    // See also https://github.com/twbs/bootstrap/issues/17695

    .modal-open & {
        overflow-x: hidden;
        overflow-y: auto;
    }
    &.show {
        display: block !important;
    }
}

// Shell div to position the modal with bottom padding
.modal-dialog {
    @extend %center-middle;
    position: relative;
    width: auto;
    // allow clicks to pass through for custom click handling to close modal
    pointer-events: none;
    display: inline-block;

    // When fading in the modal, animate it to slide down
    .modal.fade & {
        transition: $modal-transition;
    }
}

.modal-dialog-centered {
    display: flex;
    align-items: center;
    min-height: calc(100% - (#{$modal-dialog-margin} * 2));
}

// Actual modal
.modal-content {
    color: var(--modal-color);
    background-color: var(--modal-backgroundColor);
    position: relative;
    display: flex;
    flex-direction: column;
    width: 100%; // Ensure `.modal-content` extends the full width of the parent `.modal-dialog`
    // counteract the pointer-events: none; in the .modal-dialog
    pointer-events: auto;
    background-clip: padding-box;
    border: $modal-content-border-width solid $modal-content-border-color;
    border-radius: 0.3rem;
    box-shadow: $modal-content-box-shadow-xs;
    // Remove focus outline from opened modal
    outline: 0;
}

// Modal background
.modal-backdrop {
    position: fixed;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    z-index: $zindex-modal-backdrop;
    background-color: $modal-backdrop-bg;

    // Fade for backdrop
    &.fade {
        opacity: 0;
    }
    &.show {
        opacity: $modal-backdrop-opacity;
    }
}

// Modal header
// Top section of the modal w/ title and dismiss
.modal-header {
    color: var(--modal-headerColor);
    background-image: linear-gradient(var(--modal-headerBackgroundColorTop), var(--modal-headerBackgroundColorBottom));
    display: flex;
    align-items: flex-start; // so the close btn always stays on the upper right corner
    justify-content: space-between; // Put modal header elements (title and dismiss) on opposite ends
    padding: $modal-header-padding;
    border-bottom: $modal-header-border-width solid $modal-header-border-color;
    border-top-left-radius: 0.3rem;
    border-top-right-radius: 0.3rem;
    position: relative;

    .close {
        padding: $modal-header-padding;
        right: 0;
        position: absolute;
        top: 50%;
        transform: translateY(-50%);
        margin: 0;
        font-size: 30px;
    }
}

// Title text within header
.modal-title {
    margin-bottom: 0;
    line-height: $modal-title-line-height;
    font-weight: 600;
}

// Modal body
// Where all modal content resides (sibling of .modal-header and .modal-footer)
.modal-body {
    position: relative;
    // Enable `flex-grow: 1` so that the body take up as much space as possible
    // when should there be a fixed height on `.modal-dialog`.
    flex: 1 1 auto;
    padding: $modal-body-padding;
}

// Footer (for actions)
.modal-footer {
    color: var(--modal-footerColor);
    background-color: var(--modal-footerBackgroundColor);
    display: flex;
    align-items: center; // vertically center
    justify-content: flex-end; // Right align buttons with flex property because text-align doesn't work on flex items
    padding: $modal-footer-padding;
    border-top: $modal-footer-border-width solid $modal-footer-border-color;

    // Easily place margin between footer elements
    > :not(:first-child) {
        margin-left: 0.5rem;
    }

    > :not(:last-child) {
        margin-right: 0.5rem;
    }
}

// Measure scrollbar width for padding body during modal show/hide
.modal-scrollbar-measure {
    position: absolute;
    top: -9999px;
    width: 50px;
    height: 50px;
    overflow: scroll;
}

// Scale up the modal
@media (min-width: 576px) {
    // Automatically set modal's width for larger viewports
    .modal-dialog-centered {
        min-height: calc(100% - (#{$modal-dialog-margin-y-sm-up} * 2));
    }

    .modal-content {
        box-shadow: $modal-content-box-shadow-sm-up;
    }

    .modal-sm {
        max-width: $modal-sm;
    }
}

@media (min-width: 1200px) {
    .modal-lg, .modal-xl {
        max-width: $modal-lg;
    }
}

.modal-xl {
    max-width: $modal-xl;
}
</style>
